import React from "react";
import { motion } from "framer-motion";
import { MapPin, Users, Shield, Award, CheckCircle } from "lucide-react";

const Team = () => {
  const coPrincipalInvestigators = [
    {
      name: "Dr. Peter Hall",
      title: "Senior Principal Investigator",
      affiliation: "University of Waterloo",
      role: "Professor, School of Public Health Sciences & Co-Director, UW Advanced MRI and Neuromodulation (AMRI) Core Facility",
      description:
        "Dr Hall’s expertise in translational neuroscience and population health guides the overall vision and design of the Biobank Platform",
      image: require("../assets/images/team/peter-hall.jpg"),
    },
    {
      name: "Dr. Philip Awadalla",
      title: "Co-Principal Investigator",
      affiliation: "University of Oxford & University of Toronto",
      role: "Professor of Molecular Genetics & Biology",
      description:
        "His experience as Director of the CanPath Study provides critical insights for overseeing data collection and interpreting findings with relevance to predicting future cancer risk in young adults.",
      image: require("../assets/images/team/philip-awadalla.jpeg"),
    },
    {
      name: "Dr. Zahid Butt",
      title: "Co-Principal Investigator",
      affiliation: "University of Waterloo",
      role: "Associate Professor, School of Public Health Sciences & CRC in Interdisciplinary Research for Pandemic Preparedness",
      description:
        "His experience with large-scale research involving EHRs guides our strategy for tracking baseline and follow-up disease incidence.",
      image: require("../assets/images/team/zahid-butt.jpeg"),
    },
    {
      name: "Dr. Helen Chen",
      title: "Co-Principal Investigator",
      affiliation: "University of Waterloo",
      role: "Assistant Research Professor, School of Public Health Sciences",
      description:
        "Her expertise in machine learning and advanced computing infrastructure will guide the use of local advanced computing resources and data pipelines.",
      image: require("../assets/images/team/helen-chen.jpeg"),
    },
    {
      name: "Dr. Robin Duncan",
      title: "Co-Principal Investigator",
      affiliation: "University of Waterloo",
      role: "Associate Professor, Department of Kinesiology and Health Sciences",
      description:
        "Her expertise in lipid enzyme biology supports the development of the omics component of the platform, and her validated sample storage protocols will be adopted.",
      image: require("../assets/images/team/robin-duncan.jpeg"),
    },
    {
      name: "Dr. Geoffrey Fong",
      title: "Co-Principal Investigator",
      affiliation: "University of Waterloo",
      role: "University Professor of Psychology and Public Health Sciences & PI of the ITC Project",
      description:
        "His two decades of expertise will support the survey component of the CBP, having conducted international cohort surveys of tobacco use in 31 countries.",
      image: require("../assets/images/team/geoffrey-fong.jpeg"),
    },
    {
      name: "Dr. Anita Layton",
      title: "Co-Principal Investigator",
      affiliation: "University of Waterloo",
      role: "Canada 150 Research Chair in Mathematical Biology and Medicine & Professor of Applied Mathematics, Computer Science, Pharmacy, and Biology",
      description:
        "Her expertise in machine learning will guide the development of the signal processing pipeline for MRI and omics data.",
      image: require("../assets/images/team/anita-layton.jpeg"),
    },
    {
      name: "Dr. Ben Thompson",
      title: "Co-Principal Investigator",
      affiliation: "University of Waterloo",
      role: "Full Professor and Director, UW School of Optometry & Scientific Director, AMRI and Centre for Eye and Vision Research, Hong Kong",
      description:
        "His expertise in vision, TMS, and eye imaging methods will inform the planning and implementation of the optical and MRI methods.",
      image: require("../assets/images/team/ben-thompson.jpeg"),
    },
    {
      name: "Dr. Alexander Wong",
      title: "Co-Principal Investigator",
      affiliation: "University of Waterloo",
      role: "Canada Research Chair in Artificial Intelligence and Medical Imaging & Full Professor, Systems Design Engineering",
      description:
        "His extensive experience in image processing and commercialization will guide the development and implementation of the multimodal MRI and optical imaging processing pipeline.",
      image: require("../assets/images/team/alexander-wong.jpeg"),
    },
  ];

  const coInvestigators = [
    {
      name: "Dr. Mark Ferro",
      affiliation: "University of Waterloo",
      role: "Full Professor, School of Public Health Sciences & Canada Research Chair in Youth Mental Health",
      description:
        "His expertise in early life multimorbidity will guide hypothesis testing and measurement strategies in this emerging adult cohort.",
      image: require("../assets/images/team/mark-ferro.jpeg"),
    },
    {
      name: "Dr. Andrea Edginton",
      affiliation: "University of Waterloo",
      role: "Director, UW School of Pharmacy",
      description: "Her expertise will inform the EHR strategy.",
      image: require("../assets/images/team/andrea-edginton.jpeg"),
    },
    {
      name: "Dr. Rui Fu",
      affiliation: "University of Calgary",
      role: "Assistant Professor, School of Community Health Sciences",
      description:
        "Her expertise in cancer survivorship and statistical modelling will inform hypotheses and analytic strategy.",
      image: require("../assets/images/team/rui-fu.jpeg"),
    },
    {
      name: "Dr. David Hammond",
      affiliation: "University of Waterloo",
      role: "Professor, School of Public Health Sciences & Internationally Recognized Expert in Tobacco, Vaping, and Health",
      description:
        "His expertise in population health and policy research will guide measurement strategies for vaping and cannabis use.",
      image: require("../assets/images/team/david-hammond.jpeg"),
    },
    {
      name: "Dr. Hao Luo",
      affiliation: "University of Waterloo",
      role: "Assistant Professor, School of Public Health Sciences",
      description:
        "Her expertise in advanced statistical methods will guide our analytic approaches.",
      image: require("../assets/images/team/hao-luo.jpeg"),
    },
    {
      name: "Dr. James MacKillop",
      affiliation: "McMaster University",
      role: "CRC in Addictions in Mental Health",
      description:
        "His expertise will inform our substance use measurement approach.",
      image: require("../assets/images/team/james-mackillop.jpeg"),
    },
    {
      name: "Dr. John Mielke",
      affiliation: "University of Waterloo",
      role: "Associate Professor, School of Public Health Sciences",
      description:
        "His expertise will be critical for developing measurement strategies for stress related exposures and outcomes.",
      image: require("../assets/images/team/john-mielke.jpeg"),
    },
    {
      name: "Dr. Mark Oremus",
      affiliation: "University of Waterloo",
      role: "Full Professor, School of Public Health Sciences",
      description:
        "His expertise in epidemiological methods and statistics will inform the initial data analyses involving cross-sectional and prospective relationships in Phase 1 of the CBP.",
      image: require("../assets/images/team/mark-oremus.jpeg"),
    },
    {
      name: "Dr. Diana Sanchez-Ramirez",
      affiliation: "University of Manitoba",
      role: "Associate Professor of Respiratory Therapy",
      description:
        "Her expertise in long-COVID and respiratory rehabilitation will inform mechanistic PCC analyses.",
      image: require("../assets/images/team/diana-sanchez-ramirez.jpeg"),
    },
    {
      name: "Dr. Abel Torres-Espín",
      affiliation: "University of Waterloo",
      role: "Assistant Professor, School of Public Health Sciences",
      description:
        "His expertise in statistical modelling will support analysis and knowledge translation.",
      image: require("../assets/images/team/abel-torres-espin.jpeg"),
    },
    {
      name: "Dr. Tracy Vaillancourt",
      affiliation: "University of Ottawa",
      role: "Tier 1 CRC in School-Based Mental Health and Violence Prevention & PI of the OHPRS",
      description: "She will help target low-income schools for recruitment.",
      image: require("../assets/images/team/tracy-vaillancourt.jpeg"),
    },
  ];

  const TeamMemberCard = ({ member, isPrincipal = false }) => {
    // Specific adjustments for certain team members
    const getImageStyle = () => {
      const baseStyle = {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center",
      };

      // Adjust for specific team members whose hair is being cut off
      if (member.name === "Dr. Peter Hall") {
        return {
          ...baseStyle,
          objectPosition: "center",
          transform: "scale(1.2)",
          transformOrigin: "center",
        };
      }

      if (member.name === "Dr. Andrea Edginton") {
        return {
          ...baseStyle,
          objectPosition: "center 5%",
        };
      }

      if (
        member.name === "Dr. Ben Thompson" ||
        member.name === "Dr. Anita Layton" ||
        member.name === "Dr. Alexander Wong" ||
        member.name === "Dr. Tracy Vaillancourt"
      ) {
        return {
          ...baseStyle,
          objectPosition: "center 15%", // Move image up to show more of the head
        };
      }

      return baseStyle;
    };

    return (
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        style={{ textAlign: "center" }}
      >
        <div style={{ marginBottom: "1rem" }}>
          <div
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              overflow: "hidden",
              margin: "0 auto 1rem auto",
              border: "3px solid #1E40AF",
              boxShadow: "0 4px 15px rgba(30, 64, 175, 0.2)",
            }}
          >
            <img src={member.image} alt={member.name} style={getImageStyle()} />
          </div>
          <h3 style={{ color: "#1E40AF", marginBottom: "0.5rem" }}>
            {member.name}
          </h3>
          <p
            style={{ fontWeight: "600", color: "#666", marginBottom: "0.5rem" }}
          >
            {member.title || "Co-Investigator"}
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            <MapPin size={16} color="#666" />
            <p style={{ color: "#666", margin: 0 }}>{member.affiliation}</p>
          </div>
          {member.role && (
            <p
              style={{
                fontSize: "0.9rem",
                color: "#666",
                marginBottom: "1rem",
              }}
            >
              {member.role}
            </p>
          )}
          <p style={{ fontSize: "0.9rem", lineHeight: "1.5" }}>
            {member.description}
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="team-page">
      {/* Hero Section */}
      <section
        className="hero-small"
        style={{
          paddingTop: "80px",
          paddingBottom: "10px",
          backgroundImage:
            "linear-gradient(135deg, rgba(30, 64, 175, 0.9) 0%, rgba(59, 130, 246, 0.9) 100%), url(/images/young-adults.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          color: "white",
        }}
      >
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="section-title"
              style={{ color: "white" }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Our Dedicated Team
            </motion.h1>
            <motion.p
              className="section-subtitle"
              style={{ color: "rgba(255, 255, 255, 0.9)" }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            ></motion.p>
          </motion.div>
        </div>
      </section>

      {/* Co-Principal Investigators */}
      <section className="section bg-light">
        <div className="container">
          <h2
            style={{ textAlign: "center", marginBottom: "3rem", color: "#333" }}
          >
            Principal Investigators
          </h2>
          <div className="team-grid">
            {coPrincipalInvestigators.map((member, index) => (
              <TeamMemberCard key={member.name} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* Co-Investigators */}
      <section className="section">
        <div className="container">
          <h2
            style={{ textAlign: "center", marginBottom: "3rem", color: "#333" }}
          >
            Co-Investigators
          </h2>
          <div className="team-grid">
            {coInvestigators.map((member, index) => (
              <TeamMemberCard key={member.name} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* Governance Structure */}
      <section className="section bg-light">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="section-title">Governance Structure</h2>
            <p className="section-subtitle">
              The Canadian Biobank operates under a comprehensive governance
              structure ensuring scientific excellence, ethical oversight, and
              community engagement.
            </p>

            <div className="governance-grid" style={{ marginTop: "3rem" }}>
              {/* Executive Committee */}
              <motion.div
                className="governance-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="governance-icon">
                  <Users size={32} />
                </div>
                <h3>Executive Committee</h3>
                <p>
                  Led by Dr. Peter Hall (Senior Principal Investigator) and all
                  Principal Investigators, the Executive Committee provides
                  overall strategic direction and coordination.
                </p>
                <div className="operational-groups">
                  <h4>Operational Groups</h4>
                  <div className="group-list">
                    <div className="group-item">
                      <CheckCircle size={16} />
                      <span>
                        Nominated Principal Investigator (Dr. Peter Hall)
                      </span>
                    </div>
                    <div className="group-item">
                      <CheckCircle size={16} />
                      <span>Imaging Group (Dr. Ben Thompson)</span>
                    </div>
                    <div className="group-item">
                      <CheckCircle size={16} />
                      <span>Survey Group (Dr. Geoffrey Fong)</span>
                    </div>
                    <div className="group-item">
                      <CheckCircle size={16} />
                      <span>Omics Group (Dr. Philip Awadalla)</span>
                    </div>
                    <div className="group-item">
                      <CheckCircle size={16} />
                      <span>Statistical Group (Dr. Anita Layton)</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Advisory Boards */}
              <motion.div
                className="governance-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="governance-icon">
                  <Shield size={32} />
                </div>
                <h3>Advisory Boards</h3>
                <p>
                  Multiple advisory boards provide oversight and guidance across
                  different aspects of the study to ensure ethical conduct and
                  community relevance.
                </p>
                <div className="advisory-committees">
                  <h4>Advisory Committees</h4>
                  <div className="committee-list">
                    <div className="committee-item">
                      <Award size={16} />
                      <span>Scientific Advisory Board</span>
                    </div>
                    <div className="committee-item">
                      <Award size={16} />
                      <span>Operations / Data Committee</span>
                    </div>
                    <div className="committee-item">
                      <Award size={16} />
                      <span>Ethics, Equity & Engagement Committee</span>
                    </div>
                    <div className="committee-item">
                      <Award size={16} />
                      <span>Young Adult Advisory Board</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/**
       * Research Network (commented per request)
       *
       * <section className="section">
       *   <div className="container">
       *     <motion.div
       *       initial={{ opacity: 0, y: 30 }}
       *       whileInView={{ opacity: 1, y: 0 }}
       *       transition={{ duration: 0.8 }}
       *       viewport={{ once: true }}
       *       className="text-center"
       *     >
       *       <h2 className="section-title">Research Network</h2>
       *       <p className="section-subtitle">
       *         Our team spans multiple leading research institutions across Canada and internationally,
       *         bringing together diverse expertise in health research, data science, and clinical practice.
       *       </p>
       *       <div className="grid grid-3" style={{ marginTop: '2rem' }}>
       *         <div className="card text-center">
       *           <h3 style={{ color: '#1E40AF' }}>University of Waterloo</h3>
       *           <p>Lead Institution</p>
       *         </div>
       *         <div className="card text-center">
       *           <h3 style={{ color: '#1E40AF' }}>University of Oxford</h3>
       *           <p>International Partner</p>
       *         </div>
       *         <div className="card text-center">
       *           <h3 style={{ color: '#1E40AF' }}>University of Toronto</h3>
       *           <p>Collaborating Institution</p>
       *         </div>
       *       </div>
       *       <div className="grid grid-3" style={{ marginTop: '1rem' }}>
       *         <div className="card text-center">
       *           <h3 style={{ color: '#1E40AF' }}>McMaster University</h3>
       *           <p>Partner Institution</p>
       *         </div>
       *         <div className="card text-center">
       *           <h3 style={{ color: '#1E40AF' }}>University of Calgary</h3>
       *           <p>Partner Institution</p>
       *         </div>
       *         <div className="card text-center">
       *           <h3 style={{ color: '#1E40AF' }}>University of Manitoba</h3>
       *           <p>Partner Institution</p>
       *         </div>
       *       </div>
       *     </motion.div>
       *   </div>
       * </section>
       */}

      {/* Student Advisory Board */}
      <section className="section student-advisory">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="section-title">Student Advisory Board</h2>
            <div
              className="team-grid"
              style={{
                marginTop: "1.25rem",
                gap: "2rem",
                gridTemplateColumns: "repeat(4, 320px)",
                justifyContent: "center",
              }}
            >
              <div
                className="card"
                style={{
                  textAlign: "center",
                  padding: "0.5rem 0.5rem",
                  marginLeft: 0,
                  marginRight: 0,
                  width: "320px",
                  justifySelf: "center",
                }}
              >
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    margin: "0.25rem auto 1rem auto",
                    border: "3px solid #1E40AF",
                  }}
                >
                  <img
                    src={require("../assets/images/team/aisha.png")}
                    alt="Sara Shahid"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 20%",
                    }}
                  />
                </div>
                <h3 style={{ color: "#1E40AF", marginBottom: "0.25rem" }}>
                  Sara Shahid
                </h3>
                <p
                  style={{
                    fontWeight: 600,
                    color: "#666",
                    marginBottom: "0.25rem",
                  }}
                >
                  Chair
                </p>
                <p style={{ color: "#666" }}>Western University</p>
              </div>

              <div
                className="card"
                style={{
                  textAlign: "center",
                  padding: "0.5rem 0.5rem",
                  marginLeft: 0,
                  marginRight: 0,
                  width: "320px",
                  justifySelf: "center",
                }}
              >
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    margin: "0.25rem auto 1rem auto",
                    border: "3px solid #1E40AF",
                  }}
                >
                  <img
                    src={require("../assets/images/team/nina_doad.png")}
                    alt="Nina Doad"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 70%",
                    }}
                  />
                </div>
                <h3 style={{ color: "#1E40AF", marginBottom: "0.25rem" }}>
                  Nina Doad
                </h3>
                <p style={{ color: "#666" }}>McMaster University</p>
              </div>

              <div
                className="card"
                style={{
                  textAlign: "center",
                  padding: "0.5rem 0.5rem",
                  marginLeft: 0,
                  marginRight: 0,
                  width: "320px",
                  justifySelf: "center",
                }}
              >
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    margin: "0.25rem auto 1rem auto",
                    border: "3px solid #1E40AF",
                  }}
                >
                  <img
                    src={require("../assets/images/team/sara_sahid.png")}
                    alt="Aisha Mughal"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 20%",
                    }}
                  />
                </div>
                <h3 style={{ color: "#1E40AF", marginBottom: "0.25rem" }}>
                  Natalie Grindrod
                </h3>
                <p style={{ color: "#666" }}>Western University</p>
              </div>

              {/* <div
                className="card"
                style={{
                  textAlign: "center",
                  padding: "0.5rem 0.5rem",
                  marginLeft: 0,
                  marginRight: 0,
                  width: "320px",
                  justifySelf: "center",
                }}
              >
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    margin: "0.25rem auto 1rem auto",
                    border: "3px solid #1E40AF",
                  }}
                >
                  <img
                    src={require("../assets/images/team/IMG_2810.png")}
                    alt="Aisha Mughal"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center 20%",
                    }}
                  />
                </div>
                <h3 style={{ color: "#1E40AF", marginBottom: "0.25rem" }}>
                  Aisha Mughal
                </h3>
                <p style={{ color: "#666" }}>Western University</p>
              </div> */}

              <div
                className="card"
                style={{
                  textAlign: "center",
                  padding: "0.5rem 0.5rem",
                  marginLeft: 0,
                  marginRight: 0,
                  width: "320px",
                  justifySelf: "center",
                }}
              >
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    margin: "0.25rem auto 1rem auto",
                    border: "3px solid #1E40AF",
                  }}
                >
                  <img
                    src={require("../assets/images/team/AdrianSafati.jpeg")}
                    alt="Adrian Safati"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      transform: "scale(1)",
                      transformOrigin: "center",
                    }}
                  />
                </div>
                <h3 style={{ color: "#1E40AF", marginBottom: "0.25rem" }}>
                  Adrian Safati
                </h3>
                <p style={{ color: "#666" }}>University of Waterloo</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Team;
